import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Headset, RefreshCw, Send, User, Building2, MailOpen, Circle, CheckCircle2, X } from "lucide-react";

const POLL_MS = 5000;

export default function AdminSupportChat() {
    const [convs, setConvs] = useState([]);
    const [count, setCount] = useState(0);
    const [unread, setUnread] = useState(0);
    const [statusFilter, setStatusFilter] = useState("open");
    const [selectedId, setSelectedId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [selectedConv, setSelectedConv] = useState(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);

    const loadList = useCallback(async () => {
        try {
            const r = await api.get(`/admin/support/conversations${statusFilter ? `?status=${statusFilter}` : ""}`);
            setConvs(r.data.items || []);
            setCount(r.data.count || 0);
            setUnread(r.data.unread_total || 0);
        } catch { /* ignore */ }
    }, [statusFilter]);

    const loadThread = useCallback(async (id) => {
        if (!id) return;
        try {
            const r = await api.get(`/admin/support/conversations/${id}/messages`);
            setMessages(r.data.messages || []);
            setSelectedConv(r.data.conversation);
            // Refresh list so unread counter drops
            loadList();
        } catch { /* ignore */ }
    }, [loadList]);

    useEffect(() => { loadList(); }, [loadList]);
    useEffect(() => {
        const t = setInterval(() => { loadList(); if (selectedId) loadThread(selectedId); }, POLL_MS);
        return () => clearInterval(t);
    }, [loadList, loadThread, selectedId]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    const sendReply = async (e) => {
        e && e.preventDefault && e.preventDefault();
        const t = text.trim();
        if (!t || !selectedId || sending) return;
        setSending(true);
        try {
            const { data } = await api.post(`/admin/support/conversations/${selectedId}/message`, { text: t });
            setMessages((prev) => [...prev, data]);
            setText("");
        } catch { /* ignore */ }
        finally { setSending(false); }
    };

    const closeConv = async () => {
        if (!selectedId) return;
        if (!window.confirm("Chiudere questa conversazione?")) return;
        try {
            await api.post(`/admin/support/conversations/${selectedId}/close`);
            loadList();
            loadThread(selectedId);
        } catch { /* ignore */ }
    };

    return (
        <div data-testid="admin-support-chat" className="space-y-6">
            <div>
                <span className="label-eyebrow text-gold">Super Admin · Assistenza</span>
                <h2 className="font-serif text-3xl text-ink-50 mt-2 flex items-center gap-2">
                    <Headset size={22} className="text-gold" /> Chat Supporto
                </h2>
                <p className="text-ink-400 text-sm mt-1">Rispondi in tempo reale alle richieste delle agenzie. I dati identificativi (nome, email, ID account, piano) sono mostrati per ogni conversazione.</p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    {[
                        { id: "open", label: "Aperte" },
                        { id: "closed", label: "Chiuse" },
                        { id: "", label: "Tutte" },
                    ].map((f) => (
                        <button key={f.id} data-testid={`adm-sup-filter-${f.id || "all"}`} onClick={() => setStatusFilter(f.id)} className={`px-3 py-1.5 text-xs border ${statusFilter === f.id ? "border-gold text-gold" : "border-white/15 text-ink-400 hover:border-white/30"}`}>{f.label}</button>
                    ))}
                </div>
                <div className="text-xs text-ink-400">{count} conversazioni · <span className="text-gold">{unread}</span> messaggi non letti</div>
                <button data-testid="adm-sup-refresh" onClick={loadList} className="ml-auto text-ink-400 hover:text-gold p-1"><RefreshCw size={14} /></button>
            </div>

            <div className="grid grid-cols-12 gap-4 min-h-[540px]">
                {/* Lista conversazioni */}
                <div data-testid="adm-sup-list" className="col-span-12 md:col-span-4 border border-white/10 bg-ink-900/40 max-h-[560px] overflow-y-auto">
                    {convs.length === 0 ? (
                        <div className="p-8 text-center text-ink-400 text-sm">Nessuna conversazione</div>
                    ) : (
                        convs.map((c) => {
                            const active = selectedId === c.id;
                            const identity = c.identity || {};
                            const hasUnread = (c.unread_admin || 0) > 0;
                            return (
                                <button
                                    key={c.id}
                                    data-testid={`adm-sup-conv-${c.id}`}
                                    onClick={() => { setSelectedId(c.id); loadThread(c.id); }}
                                    className={`w-full text-left p-3 border-b border-white/5 flex flex-col gap-1 transition-colors ${active ? "bg-gold/10 border-l-2 border-l-gold" : "hover:bg-white/5"}`}
                                >
                                    <div className="flex items-center gap-2 justify-between">
                                        <div className="text-sm text-ink-50 truncate flex-1 flex items-center gap-1.5">
                                            {hasUnread && <Circle size={7} className="text-gold fill-gold flex-shrink-0" />}
                                            <span className="truncate">{identity.agency_name || identity.name || identity.email || c.agency_id}</span>
                                        </div>
                                        {hasUnread && <span className="text-[10px] bg-gold text-black px-1.5 py-0.5 tabular-nums">{c.unread_admin}</span>}
                                    </div>
                                    <div className="text-[10px] text-ink-500 truncate">{identity.email}</div>
                                    <div className="text-[10px] text-ink-400 truncate">{c.last_message_preview || "—"}</div>
                                    <div className="flex items-center gap-2 text-[9px] text-ink-500 uppercase tracking-wider mt-1">
                                        <span className={`px-1.5 py-0.5 ${c.status === "open" ? "bg-green-900/40 text-green-300" : "bg-white/5 text-ink-400"}`}>{c.status}</span>
                                        {identity.plan_id && <span className="text-gold">{identity.plan_id}</span>}
                                        <span className="ml-auto">{c.last_message_at ? new Date(c.last_message_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>

                {/* Thread */}
                <div className="col-span-12 md:col-span-8 border border-white/10 bg-ink-900/40 flex flex-col max-h-[560px]">
                    {!selectedConv ? (
                        <div className="flex-1 flex items-center justify-center text-ink-400 text-sm p-8 text-center">Seleziona una conversazione dalla lista a sinistra.</div>
                    ) : (
                        <>
                            {/* Identity header */}
                            <div className="p-4 border-b border-white/10 bg-black/30">
                                <div className="flex items-center gap-3 mb-2">
                                    <Building2 size={16} className="text-gold" />
                                    <div className="font-serif text-lg text-ink-50">{selectedConv.identity?.agency_name || selectedConv.identity?.name || "—"}</div>
                                    {selectedConv.status === "closed" && <span className="text-[9px] uppercase tracking-wider bg-red-900/40 text-red-300 px-2 py-0.5">Chiusa</span>}
                                    <button data-testid="adm-sup-close-conv" onClick={closeConv} className="ml-auto text-[10px] uppercase tracking-wider border border-white/15 hover:border-red-400 hover:text-red-300 text-ink-400 px-2 py-1 flex items-center gap-1" disabled={selectedConv.status === "closed"}>
                                        <X size={11} /> Chiudi
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-[11px] text-ink-400" data-testid="adm-sup-identity">
                                    <div><span className="text-ink-500">Email:</span> <span className="text-ink-100">{selectedConv.identity?.email || "—"}</span></div>
                                    <div><span className="text-ink-500">ID Account:</span> <span className="text-ink-100 font-mono">{selectedConv.identity?.user_id}</span></div>
                                    <div><span className="text-ink-500">Piano:</span> <span className="text-gold uppercase">{selectedConv.identity?.plan_id || "—"}</span></div>
                                    <div><span className="text-ink-500">Ruolo:</span> <span className="text-ink-100">{selectedConv.identity?.role || "—"}</span></div>
                                    {selectedConv.identity?.phone && <div><span className="text-ink-500">Tel:</span> <span className="text-ink-100">{selectedConv.identity.phone}</span></div>}
                                </div>
                            </div>

                            <div ref={scrollRef} data-testid="adm-sup-messages" className="flex-1 overflow-y-auto p-4 space-y-3">
                                {messages.length === 0 ? (
                                    <div className="text-center text-ink-500 text-xs py-8">Nessun messaggio in questa conversazione.</div>
                                ) : (
                                    messages.map((m) => {
                                        const staff = m.sender === "admin";
                                        return (
                                            <div key={m.id} className={`flex ${staff ? "justify-end" : "justify-start"}`}>
                                                <div
                                                    data-testid={staff ? "adm-sup-bubble-out" : "adm-sup-bubble-in"}
                                                    className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl break-words whitespace-pre-wrap shadow-[0_4px_10px_rgba(0,0,0,0.28),0_1px_2px_rgba(0,0,0,0.18)] ${staff ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-900 border border-gray-200"}`}
                                                >
                                                    <div className={`text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1 ${staff ? "text-white/80" : "text-gray-500"}`}>
                                                        {staff ? <><CheckCircle2 size={10} /> Staff</> : <><User size={10} /> {m.sender_label || "Agenzia"}</>}
                                                        <span className="ml-auto">{new Date(m.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                                                    </div>
                                                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            <form onSubmit={sendReply} className="p-3 border-t border-white/10 flex gap-2">
                                <input
                                    data-testid="adm-sup-input"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder="Rispondi all'agenzia…"
                                    disabled={selectedConv.status === "closed"}
                                    className="flex-1 bg-transparent border border-white/15 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none disabled:opacity-50"
                                />
                                <button data-testid="adm-sup-send" type="submit" disabled={sending || selectedConv.status === "closed"} className="bg-gold text-black px-4 hover:bg-gold-soft disabled:opacity-50 flex items-center gap-2 text-sm"><Send size={14} /> Invia</button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
